<template>
	<div id="app">
	    <!-- 全局组件，全部页面可用 -->
	    <Toaster position="top-center" theme="dark" />
	    <Spinner :isScreen="true" v-if="isRoutePending" />
	
	    <!-- min‑h‑full保证无论是否隐藏布局，外层最小占满屏幕 -->
	    <div
	      class="w-full min-h-full"
	      :class="[
	        !$route.meta.hiddenLayout ? 'app-container h-full' : '',
	        !$route.meta.hiddenLayout && hideBottom ? 'hide-bottom' : ''
	      ]"
	    >
	      <router-view v-slot="{ Component }">
	        <KeepAlive>
	          <component :is="Component" />
	        </KeepAlive>
	      </router-view>
	    </div>
	  </div>
</template>

<script setup>
	import {
		computed,
		onMounted,
		onUnmounted,
		provide,
		ref,
		watch
	} from "vue";
	import {
		useRoute,
		useRouter
	} from "vue-router";
	import {
		deviceType
	} from "vue-device-detect";
	import i18n from "@/i18n";
	import Spinner from "components/customUI/spinner/index.vue";
	import {
		useUserStore
	} from "@/stores/user";
	import {
		requestNotificationPermission
	} from "@/utils/message";
	import {
		fixIphoneTransitionOnBfcache,
		optimizeForBfcache
	} from "@/utils/bfcache";
	import {
		toast
	} from "vue-sonner";
	import {
		Session,
		Local,
		STORAGE_KEYS
	} from "utils/storage";
	import {
		fetchTenantByDomain
	} from "@/api/login/index";
	import {
		useClientConfig
	} from "@/composables/useClientConfig";
	import {
		Crisp
	} from 'crisp-sdk-web'
	const userStore = useUserStore();
	// global loading
	const route = useRoute();
	const router = useRouter();
	const isRoutePending = ref(true);
	watch(useRoute, () => {
		isRoutePending.value = true;
	});
	router.beforeEach((to, from, next) => {
		isRoutePending.value = true;
		next();
	});
	router.afterEach(() => {
		isRoutePending.value = false;
	});
	console.log(deviceType)
	// 不能只信 UA：PC 开 DevTools 手机模式 UA 仍是 desktop，必须看当前窗口宽
	const windowWidth = ref(window.innerWidth);
	const device = ref(
		deviceType == "mobile" || deviceType == "tablet" || windowWidth.value < 640
			? "mobile"
			: "desktop"
	);
	// 窄屏（含刚判成 mobile）默认收起侧栏
	const miniMenu = ref(device.value === "mobile" || windowWidth.value < 1280);
	const {
		customerService
	} = useClientConfig();

	const websiteId = computed(() => customerService.value?.websiteId || '');
	watch(websiteId, (newId) => {
		if (!newId) return;
		Crisp.setTokenId();
		Crisp.session.reset();
		// ✅修复configure参数，只能传一个对象
		Crisp.configure(newId, {
			lockMaximalized: false,
		});
		Crisp.chat.hide();
	}, {
		immediate: false
	});
	onMounted(async () => {
		window.addEventListener("resize", handleResize);
		// 启动立刻按当前宽度校正 device（只 addListener 不会触发）
		handleResize();
		requestNotificationPermission();
		// 初始化 bfcache 相关优化
		optimizeForBfcache();
		fixIphoneTransitionOnBfcache();

	});

	onUnmounted(() => {
		window.removeEventListener("resize", handleResize);
	});

	const handleResize = () => {
		windowWidth.value = window.innerWidth;

		if (windowWidth.value < 640) {
			device.value = "mobile";
			miniMenu.value = true;
			setDeviceToHtml(device.value);
		} else {
			device.value = "desktop";
			setDeviceToHtml(device.value);
			if (windowWidth.value < 1280) {
				miniMenu.value = true;
			} else {
				miniMenu.value = false;
			}
		}
	};

	const setDeviceToHtml = (device) => {
		const html = document.querySelector("html");
		html.setAttribute("device", device);
		const body = document.querySelector("body");
		body.setAttribute("device", device);
	};

	const switchMiniMenu = (value) => {
		miniMenu.value = value;
	};
	//浏览器指纹
	useUserStore().getBrowserId()

	provide("device", device);
	provide("miniMenu", miniMenu);
	provide("switchMiniMenu", switchMiniMenu);

	const hideBottom = computed(() => {
		return route.path.includes("/sports");
	});

	// Dialog

	// lang
	provide("locale", i18n.global.locale);
	provide("langVis", ref(false));

	// mobile menu
	provide("menuVis", ref(false));

	// notification
	provide("notificationVis", ref(false));

	// login
	const isLogined = ref(
		localStorage.getItem("isLogined") == "true" ? true : false
	);

	provide("isLogined", isLogined);
	const loginVis = ref(false);
	provide("loginVis", loginVis);
	// console.log(isLogined)
	// 通过当前访问域名解析租户ID，解析完成后再初始化用户信息
	// 确保所有后续请求携带正确的 TENANT-ID header
	(async () => {
		const domainInvalidPath = '/domain-invalid';
		if (window.location.pathname === domainInvalidPath) {
			return;
		}

		let tenantResolved = false;
		try {
			const res = await fetchTenantByDomain(window.location.host);
			if (res?.data) {
				useUserStore().updateTenantInfo(res.data, '');
				tenantResolved = true;
			} else {
				// 后端明确返回空：域名确实没登记，跳未授权页
				Local.remove(STORAGE_KEYS.TENANT_ID);
				Session.remove(STORAGE_KEYS.TENANT_ID);
				router.replace(domainInvalidPath);
				return;
			}
		} catch (e) {
			// 接口异常多为后端连不上/超时，跟"域名没配"不是一回事，带 reason 区分文案，页面仍自动重试自愈
			Local.remove(STORAGE_KEYS.TENANT_ID);
			Session.remove(STORAGE_KEYS.TENANT_ID);
			router.replace(domainInvalidPath + '?reason=server-error');
			return;
		}

		// TENANT-ID 就位后再拉取客户端配置，避免首次请求缺租户头
		useClientConfig().initClientConfig();

		if (tenantResolved && isLogined.value) {
			useUserStore().setUserInfos().catch((err) => {
				// 密码过期不清除登录状态，弹窗已由 request.js 拦截器触发
				if (err?.code === 4011) return;
				// 其他错误（token过期等），重置登录状态
				localStorage.removeItem("isLogined");
				isLogined.value = false;
			});
		}
	})();

	// register
	provide("registerVis", ref(false));

	// more games
	provide("moreGamesVis", ref(false));
	provide("moreGamesTitle", ref(""));
	provide("currentGame", ref(""));
	provide("currentGameType", ref(""));
	provide("gamePCinfo", ref(""));
	// more providers
	provide("moreProvidersVis", ref(false));
	// more recommends
	provide("moreRecommendsVis", ref(false));
	// Hot
	provide("hotVis", ref(false));
	// Recent
	provide("recentVis", ref(false));

	// collection
	provide("collectionsVis", ref(false));
	// Revenge
	provide("revengeVis", ref(false));
	// Deposit
	provide("depositVis", ref(false));
	// Withdraw
	provide("withdrawVis", ref(false));
	// Withdraw
	provide("luckyVis", ref(false));
	// game details
	provide("gameDetailsVis", ref(false));
	// bonus games
	provide("bonusGamesVis", ref(false));
	// Choose Payout Currency
	provide("CPCVis", ref(false));
	// bonus
	provide("bonusDetailsVis", ref(false));
	provide("checkInRuleVis", ref(false));
	provide("checkInDetailsVis", ref(false));
	provide("dailyLossbackRuleVis", ref(false));
	provide("levelUpBonusRuleVis", ref(false));
	provide("dailyRakebackRuleVis", ref(false));
	provide("weeklyLossbackRuleVis", ref(false));
	provide("specialDetailsVis", ref(false));
	provide("redeemBonusRuleVis", ref(false));
	provide("wagerProfitRuleVis", ref(false));
	// rollover
	provide("rolloverVis", ref(false));
	// redeem code
	provide("redeemCodeVis", ref(false));
	// search
	provide("searchVis", ref(false));
	// wallet
	provide("walletVis", ref(false));
	// transaction
	provide("transactionVis", ref(false));
	// myBet
	provide("myBetVis", ref(false));
	// vault
	provide("vaultVis", ref(false));
	provide("vaultHistoryVis", ref(false));
	// settings
	provide("mobileSettingsVis", ref(false));
	provide("mobileAccountInfoVis", ref(false));
	provide("mobileSecurityVis", ref(false));
	provide("mobileVerifyVis", ref(false));
	provide("contactEmailVis", ref(false));
	provide("contactPhoneVis", ref(false));
	provide("contactBirthdayVis", ref(false));
	provide("changePasswordVis", ref(false));
	provide("changePasswordVis", ref(false));
	provide("withdrawPasswordVis", ref(false));
	// 密码过期强制弹窗
	const passwordExpiredVis = ref(false);
	provide("passwordExpiredVis", passwordExpiredVis);
	// 注册全局回调，供 request.js 拦截器触发
	window.__PASSWORD_EXPIRED_CALLBACK = () => {
		passwordExpiredVis.value = true;
	};
	// 弹窗关闭时重置防重复标记
	watch(passwordExpiredVis, (val) => {
		if (!val) {
			window.__PASSWORD_EXPIRED_TRIGGERED = false;
		}
	});
	// Token 过期回调
	window.__TOKEN_EXPIRED_CALLBACK = () => {
		Session.clear();
		localStorage.removeItem("isLogined");
		isLogined.value = false;
		loginVis.value = true;
		useUserStore().$reset();
		toast.error('Your credentials have expired, please log in again');
	};
	// 登录成功后重置防重复标记
	watch(isLogined, (val) => {
		if (val) {
			window.__TOKEN_EXPIRED_TRIGGERED = false;
		}
	});
	provide("verificationEmailVis", ref(false));
	provide("verificationPhoneVis", ref(false));
	provide("verificationBirthdayVis", ref(false));
	provide("personalInfoVis", ref(false));
	provide("kycVerificationVis", ref(false));
	provide("kycVerificationVis2", ref(false));
	// profile
	provide("profileVis", ref(false));
	provide("profileEditVis", ref(false));
	// closeDownloadTip
	provide("closeDownloadTip", ref(false));
	//vip
	provide("vipLevelBenefitsVis", ref(false));
	// mobile game bonus
	provide("mobileGameBonusVis", ref(false));
</script>